import type { CityConfig } from "@/pages/city-template/types";

export const estacadaConfig: CityConfig = {
  city: "Estacada",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "estacada",
  route: "/cities/estacada",
  title: "Emergency Passport Estacada OR | Expedited Passport Services Clackamas County",
  metaDescription: "Emergency passport and expedited passport renewal in Estacada, OR — as fast as 24 hours. Fast Passport Center serves Estacada and rural Clackamas County travelers. Registered State Dept. courier. A+ BBB rated. No office visit required.",
  h1: "Emergency & Expedited Passport Services in Estacada, Oregon",
  heroSubtitle: "Passport expired before your Mt. Hood vacation turns international or a last-minute trip comes up? Fast Passport Center helps Estacada and rural Clackamas County residents get their emergency passport or expedited renewal done fast — as fast as 24 hours. A+ BBB rated.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Estacada%20Oregon%20rural%20small%20town%20with%20Clackamas%20River%20flowing%20through%20lush%20Pacific%20Northwest%20evergreen%20forest%2C%20majestic%20Mount%20Hood%20snowcapped%20peak%20visible%20in%20the%20distance%2C%20dramatic%20Pacific%20Northwest%20landscape%20with%20towering%20Douglas%20fir%20trees%2C%20golden%20afternoon%20light%20casting%20warm%20tones%20across%20river%20valley%2C%20photorealistic%20landscape%20photography%2C%20dramatic%20sky%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=estacada-or-hero-01&orientation=landscape",
  heroImageAlt: "Estacada Oregon Clackamas River with Mount Hood visible in the distance through Pacific Northwest forest",
  geoPosition: "45.2901;-122.3343",
  geoPlacename: "Estacada, Oregon",
  lastModified: "2026-06-04",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "300 5th Ave, Suite 600, Seattle, WA 98104",
    distance: "About 200 miles north — 3.5 hours by car",
  },
  nearbyCitiesZips: [
    { name: "Estacada", zips: "97023" },
    { name: "Sandy", zips: "97055" },
    { name: "Molalla", zips: "97038" },
    { name: "Oregon City", zips: "97045" },
    { name: "Clackamas", zips: "97015" },
    { name: "Happy Valley", zips: "97086" },
    { name: "Gladstone", zips: "97027" },
    { name: "Damascus", zips: "97089" },
    { name: "Boring", zips: "97009" },
    { name: "Gresham", zips: "97030, 97080" },
    { name: "Portland", zips: "97201–97299" },
    { name: "Canby", zips: "97013" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online from Rural Clackamas County", desc: "No need to drive to Portland to start. Complete your emergency or expedited passport order entirely online from Estacada." },
    { icon: "ri-truck-line", title: "Ship Overnight from Nearby FedEx or UPS", desc: "Sandy, Gresham, and Oregon City all have overnight shipping options accessible to Estacada residents." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every expedited passport application is reviewed by our team before submission — critical for rural applicants who may have fewer local resources." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the Seattle Passport Agency — no public appointment hunting needed from rural Clackamas County." },
  ],
  testimonials: [
    {
      name: "Jake H.",
      location: "Estacada, OR",
      rating: 5,
      text: "Hunting trip to British Columbia came up with 10 days notice and my passport had expired five years ago. Fast Passport Center was incredible — I shipped from the Sandy FedEx and had my renewed passport in 7 days. Made the trip with a day to spare.",
      service: "Emergency Passport",
    },
    {
      name: "Melissa C.",
      location: "Estacada, OR",
      rating: 5,
      text: "Our family planned a trip to Cancún and I realized our youngest had never had a passport. Fast Passport Center walked us through the whole child passport process — what to bring to the acceptance clerk, both parents consenting, everything. Passport came in 9 days.",
      service: "Child Passport",
    },
    {
      name: "Brian T.",
      location: "Molalla, OR",
      rating: 5,
      text: "Business trip to Vancouver BC came up suddenly. I live in rural Clackamas County and was worried the post office would take months. Fast Passport Center handled everything and I got my expedited renewal in 6 days. Shipped from Oregon City.",
      service: "Expedited Passport Renewal",
    },
  ],
  facilities: [
    {
      id: "estacada-post",
      name: "Estacada Post Office",
      type: "USPS Passport Acceptance",
      address: "315 S Broadway St",
      city: "Estacada",
      state: "OR",
      zip: "97023",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Downtown Estacada — primary local acceptance location",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance facility in Estacada. Appointment required. Small office — appointments may be limited. Check availability well in advance.",
      icon: "ri-mail-line",
    },
    {
      id: "sandy-post",
      name: "Sandy Post Office",
      type: "USPS Passport Acceptance",
      address: "37323 Proctor Blvd",
      city: "Sandy",
      state: "OR",
      zip: "97055",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Sandy — about 12 miles northwest, larger office",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Larger Sandy Post Office with better appointment availability than the Estacada office during peak season.",
      icon: "ri-mail-line",
    },
    {
      id: "oregon-city-post",
      name: "Oregon City Post Office",
      type: "USPS Passport Acceptance",
      address: "610 7th St",
      city: "Oregon City",
      state: "OR",
      zip: "97045",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Oregon City — about 20 miles west",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Largest nearby passport acceptance location for Estacada residents — better photo resources and appointment availability.",
      icon: "ri-mail-line",
    },
    {
      id: "clackamas-county-clerk",
      name: "Clackamas County Clerk — Passport Services",
      type: "County Passport Acceptance",
      address: "1710 Red Soils Ct, Suite 100",
      city: "Oregon City",
      state: "OR",
      zip: "97045",
      phone: "503-655-8551",
      hours: "Mon–Fri 8:30am–4:30pm (by appointment)",
      distance: "Oregon City — about 20 miles west",
      website: "https://www.clackamas.us/recording",
      websiteLabel: "Clackamas County Clerk",
      notes: "County clerk passport acceptance — often has faster appointment availability than USPS. Worth calling ahead.",
      icon: "ri-government-line",
    },
    {
      id: "gresham-post",
      name: "Gresham Post Office",
      type: "USPS Passport Acceptance",
      address: "600 NW Division St",
      city: "Gresham",
      state: "OR",
      zip: "97030",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Gresham — about 22 miles northwest",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Gresham is a larger city with better appointment availability and photo services for Estacada residents.",
      icon: "ri-mail-line",
    },
    {
      id: "milwaukie-post",
      name: "Milwaukie Post Office",
      type: "USPS Passport Acceptance",
      address: "11122 SE Oak St",
      city: "Milwaukie",
      state: "OR",
      zip: "97222",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Milwaukie — about 25 miles northwest",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Milwaukie option for Estacada residents traveling through the Portland south metro corridor.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport from Estacada, Oregon?",
      a: "Yes. Through Fast Passport Center, Estacada residents can get an emergency passport in as little as 24 hours for urgent travel, or 2–9 business days with expedited processing. Start your order online and we send you shipping instructions — ship overnight from Sandy, Gresham, or Oregon City and we handle the rest. No office visit to our facility required.",
    },
    {
      q: "How do rural Clackamas County residents get expedited passport service?",
      a: "Estacada and rural Clackamas County residents use Fast Passport Center by starting their order online, then shipping documents overnight from the nearest FedEx or UPS location — Sandy, Gresham, or Oregon City all work. We handle all agency submission and expedited processing remotely. No drive to Portland or Seattle required.",
    },
    {
      q: "Where do Estacada residents go for passport acceptance?",
      a: "The Estacada Post Office is the closest acceptance facility, but has limited appointment availability — especially in peak season. Sandy Post Office and Oregon City Post Office (both within 20 miles) have better availability. The Clackamas County Clerk's office in Oregon City also accepts passport applications by appointment.",
    },
    {
      q: "How fast can I renew my passport from Estacada?",
      a: "Expedited passport renewal from Estacada is available through Fast Passport Center in as little as 3 business days. Ship your renewal documents overnight from Sandy or Gresham FedEx or UPS — we handle the rest. Most adult renewals do not require an in-person acceptance visit.",
    },
    {
      q: "Can I get a same-day passport near Estacada?",
      a: "Same-day passport appointments at the Seattle Passport Agency are only for life-or-death emergencies. For most Estacada residents, Fast Passport Center's 24-hour emergency processing (when eligible) or 2–9 business day expedited processing is the fastest realistic option — without the 3.5-hour drive each way to Seattle.",
    },
    {
      q: "Do I need to drive to Portland for an expedited passport from Estacada?",
      a: "No. With Fast Passport Center, you start online, we send your checklist, and you ship overnight from Sandy, Gresham, or Oregon City — we do not require a visit to our facility. We submit through reserved Seattle agency channels so you never need to make the Seattle or Portland passport office drive.",
    },
    {
      q: "What documents do I need for an expedited passport renewal from Estacada?",
      a: "For a passport renewal (if your previous passport was issued at age 16 or older within the last 15 years), you typically need your previous passport, a new passport photo, a completed DS-82 form, and payment. Fast Passport Center sends you a personalized checklist based on your specific situation so nothing is missed.",
    },
    {
      q: "How long does standard passport processing take from Estacada?",
      a: "Standard USPS passport processing out of Estacada runs 6–13 weeks — and during summer or holiday travel season, it can stretch to 14+ weeks. If your trip is in under 10 weeks, you need Fast Passport Center's expedited service.",
    },
    {
      q: "Can Estacada residents get an expedited child passport?",
      a: "Yes, though child passports (for minors under 16) require an in-person acceptance step with parental consent — this cannot be skipped. We recommend the Oregon City Post Office or Clackamas County Clerk for Estacada families since they have better acceptance appointment availability. Once the acceptance step is done, Fast Passport Center handles expedited processing.",
    },
    {
      q: "What airport do Estacada residents use for international travel?",
      a: "Portland International Airport (PDX) is the nearest major international airport for Estacada travelers, about 28 miles northwest. PDX offers direct international service to Amsterdam, London, Reykjavik, and Vancouver BC. A valid U.S. passport is required for all international departures from PDX.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Sandy", address: "37617 Hwy 26, Sandy, OR 97055", hours: "Daily 8 AM – 10 PM" },
    { name: "Walmart — Gresham", address: "2255 NE Burnside Rd, Gresham, OR 97030", hours: "Daily 7 AM – 11 PM" },
    { name: "Walgreens — Gresham", address: "1501 NW Burnside Rd, Gresham, OR 97030", hours: "Daily 8 AM – 10 PM" },
    { name: "FedEx Office — Gresham", address: "1501 NE Halsey St, Gresham, OR 97230", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 9 AM – 6 PM" },
    { name: "CVS — Oregon City", address: "19000 Molalla Ave, Oregon City, OR 97045", hours: "Daily 7 AM – 10 PM" },
    { name: "Walgreens — Oregon City", address: "19000 S Beavercreek Rd, Oregon City, OR 97045", hours: "Daily 8 AM – 10 PM" },
    { name: "Walmart — Clackamas", address: "16301 SE 82nd Dr, Clackamas, OR 97015", hours: "Daily 7 AM – 11 PM" },
    { name: "Target — Happy Valley", address: "9190 SE 82nd Ave, Happy Valley, OR 97086", hours: "Daily 8 AM – 10 PM" },
    { name: "FedEx Office — Portland SE", address: "810 SW Broadway, Portland, OR 97205", hours: "Mon–Fri 7 AM – 10 PM" },
    { name: "The UPS Store — Sandy", address: "17045 SE Stark St, Sandy, OR 97055", hours: "Mon–Fri 8 AM – 7 PM, Sat 9 AM – 5 PM" },
  ],
  dmvLocations: [
    {
      name: "Oregon City Driver & Motor Vehicle Services",
      address: "1021 S Molalla Ave, Oregon City, OR 97045",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Nearest full DMV for Estacada residents — Oregon City. Driver licenses, REAL ID, ID cards.",
      featured: true,
    },
    {
      name: "Clackamas DMV Office",
      address: "5060 SE Hwy 212, Clackamas, OR 97015",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Clackamas office — sometimes shorter waits than Oregon City.",
      featured: false,
    },
    {
      name: "Gresham Driver & Motor Vehicle Services",
      address: "285 W Powell Blvd, Gresham, OR 97030",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Gresham DMV — alternative northwest option for Estacada residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "Primary international airport — about 28 miles northwest",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "PDX is the nearest major international departure point for Estacada travelers, offering direct flights to Amsterdam, London, Reykjavik, Tokyo, and Vancouver BC. Peak season is May–August. Confirm your passport is valid (including the six-month validity rule) before booking any international flight.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: true,
    },
    {
      code: "SEA",
      name: "Seattle-Tacoma International Airport",
      distance: "Alternative hub — about 200 miles north",
      address: "17801 International Blvd, SeaTac, WA 98158",
      note: "Some Estacada-area travelers use SEA for additional international options or Alaskan cruise departures. Passport required for all international flights.",
      flightUrl: "https://www.portseattle.org/sea-tac",
      flightLabel: "SEA Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport — Estacada & Rural Clackamas County",
      description: "Outdoor adventure trip to British Columbia? Family emergency abroad? International opportunity with 48 hours notice? Our emergency passport service is available to Estacada and rural Clackamas County residents — start online, ship overnight, done.",
      bullets: ["As fast as 24 hours processing", "No drive to Seattle required", "Dedicated agent and live tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Expedited Passport Renewal",
      description: "Passport expired before your trip? Renewal from rural Clackamas County is simple — start online, ship from Sandy or Gresham overnight, and we handle the expedited agency submission.",
      bullets: ["Fastest renewal option for Estacada residents", "Ship from Sandy, Gresham, or Oregon City FedEx/UPS", "Professional document review"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport before a hunting trip to British Columbia or an international flight? We guide Estacada travelers through DS-64 and expedited replacement — urgency handled.",
      bullets: ["DS-64 form guidance included", "Expedited replacement available", "Document review to prevent rejection"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport — Rural Oregon Families",
      description: "Child passports require an in-person acceptance step near Estacada — Sandy or Oregon City post offices work well. We provide the checklist; you do the acceptance step; we expedite from there.",
      bullets: ["Both parents typically required to consent", "Acceptance facility guide for Estacada area", "Expedited processing after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "First-Time Passport Applications",
      description: "First-time passport in Estacada — we tell you exactly where to go for the acceptance clerk step and guide you through every form and document. Nothing left to guesswork.",
      bullets: ["Step-by-step guidance for rural applicants", "Acceptance facility guide", "Expedited processing after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Registered State Dept. Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier. We submit through reserved agency channels — not public lines — with rates 30–100% less than FedEx Office, Staples, and other resellers.",
      bullets: ["State Dept. registered courier", "Best price guarantee", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Other Passport Services",
      description: "Passport name change after marriage or divorce, damaged passport replacement, second passports for frequent international travel — all available with expedited courier access.",
      bullets: ["Name change after marriage or divorce", "Damaged passport replacement", "Second passport for frequent travel"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Same-day emergency passport processing for Estacada, Oregon travelers with urgent departure needs, as fast as 24 hours." },
    { name: "Expedited Passport", description: "Expedited passport processing for Estacada and rural Clackamas County residents needing passports faster than standard government processing." },
    { name: "Passport Renewal", description: "Fast passport renewal for Estacada, Oregon residents — ship from Sandy or Gresham FedEx/UPS overnight, we handle the agency submission." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement for Estacada travelers with lost or stolen passports before travel." },
    { name: "Child Passport", description: "Expedited child passport services for Estacada-area families with minors under 16." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier passport submission serving Estacada, Oregon and rural Clackamas County." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Assuming Rural Areas Have Faster Post Office Service", description: "Small post offices in Estacada can have longer appointment waits and less predictable processing than larger city offices. Standard processing still runs 6–13 weeks regardless of location — expedited courier service is the only way to guarantee speed." },
    { icon: "ri-camera-off-line", title: "Wrong or Rejected Passport Photo", description: "Rural residents sometimes use grocery store or small-format photo printers that don't meet passport specs. Fast Passport Center reviews your photo before submission — a rejected photo costs weeks." },
    { icon: "ri-file-damage-line", title: "Incomplete or Incorrectly Signed Forms", description: "One missing signature or incorrect date gets your application returned — costing weeks. Our pre-submission document review catches these before they happen." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Type", description: "Many Oregon residents — especially those born in other states — have short-form birth certificates that are not accepted. We specify exactly what you need." },
    { icon: "ri-calendar-close-line", title: "Thinking the Estacada Post Office Is Fast Enough", description: "Standard passport processing runs 6–13 weeks everywhere in the country, including Estacada. If your trip is in under 10 weeks, use Fast Passport Center's expedited service." },
    { icon: "ri-map-pin-time-line", title: "Not Accounting for Six-Month Validity Rules", description: "Many countries require your passport to be valid six months beyond your return date. A passport expiring in five months is effectively already expired for most international travel." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your travel date. Complete your secure order entirely online from Estacada." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We send your document checklist based on your specific situation — exactly what to gather, with notes for rural Clackamas County applicants." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Overnight from Sandy, Gresham, or Oregon City", description: "Drop off at any FedEx or UPS in Sandy, Gresham, or Oregon City — we provide specific location recommendations based on your address." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit your application through our reserved Seattle Passport Agency slots — no public appointment system, no waiting for cancellations." },
    { number: "05", icon: "ri-passport-line", title: "Passport Shipped to Your Estacada Address", description: "Your completed passport is shipped directly to your address. Track in real time — no guessing." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Must drive to Seattle — 3.5 hrs each way",
        "Life-or-death emergencies only",
        "Very limited availability",
        "Not practical for most Estacada residents",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable processing",
        "Ship from Sandy or Gresham overnight",
        "Document review included",
        "Dedicated agent on your case",
        "Daily status updates",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Document review included",
        "Dedicated agent assigned",
        "Daily updates",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "Standard mail — no courier access",
        "6–13 weeks average; rural offices may have longer waits",
        "In-person clerk appointment required",
        "No document review",
        "No dedicated agent",
        "Cannot upgrade after submission",
        "Not suitable if travel is under 10 weeks away",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited slots, weeks out in Seattle", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Must drive to Seattle (3.5 hours each way from Estacada)", fpc: "Ship overnight from Sandy or Gresham" },
    { feature: "Processing Speed", agency: "Strict eligibility — urgent travel only", fpc: "As fast as 24 hours — multiple tiers" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Rural Convenience", agency: "Not designed for rural Oregon residents", fpc: "Fully remote — designed for rural applicants" },
    { feature: "Government Shutdowns", agency: "May be impacted", fpc: "Not affected" },
    { feature: "Pricing", agency: "Govt. fees + 7-hour round trip to Seattle", fpc: "Best price guarantee — no travel cost" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Emergency passport services in Oregon City", route: "/cities/oregon-city" },
    { name: "Expedited passport help in Sandy", route: "/cities/sandy" },
    { name: "Fast passport renewal near Gresham", route: "/cities/gresham" },
    { name: "Rush passport services in Gladstone", route: "/cities/gladstone" },
    { name: "Expedited passports for Clackamas County travelers", route: "/states/oregon" },
  ],
  fpcOfficeNote: "After placing your online order, we send your personalized checklist and overnight shipping instructions. Ship from the Sandy, Gresham, or Oregon City FedEx or UPS location that works best for you — most are open evenings. No visit to our processing facility required.",
  fpcOfficeDistance: "Serving Estacada and rural Clackamas County remotely",
  serviceAreaIntro: "Whether you are in Estacada, Sandy, Molalla, Boring, or anywhere in rural Clackamas County, Fast Passport Center makes emergency and expedited passport processing accessible — start online, ship overnight from the nearest FedEx or UPS, and we handle the agency submission.",
  nearbyCitiesIntro: "We serve all Estacada, Sandy, and Molalla-area zip codes. Ship overnight from Sandy or Gresham and your documents arrive at our processing center the next morning.",
  howItWorksIntro: "Getting an emergency or expedited passport from rural Clackamas County is simpler than you think — no drives to Portland, no Seattle day trips. Most Estacada clients never visit an office.",
  mistakesIntro: "These are the most common mistakes that cause delays and rejections for rural Oregon passport applicants. We review your documents before submission to prevent them.",
  testimonialsIntro: "Real reviews from Estacada, Sandy, and rural Clackamas County travelers who needed their passports fast.",
  faqIntro: "The most common questions Estacada and rural Clackamas County residents ask about emergency and expedited passport services.",
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
  travelTiming: {
    title: "Travel & Passport Timing for Estacada and Rural Clackamas County Residents",
    intro: "Estacada is the gateway to the Mt. Hood National Forest and the Clackamas River recreation corridor — and its residents travel in patterns that create specific passport urgency windows. From outdoor adventures crossing into British Columbia to rural families discovering expired passports before international trips, here's what we see most.",
    cards: [
      {
        icon: "ri-landscape-line",
        title: "Outdoor Adventure & Canada Border Crossings",
        subtitle: "Hunting, fishing, skiing, and recreation trips to British Columbia",
        description: "Estacada residents are among Oregon's most active outdoor recreationists — hunting in British Columbia, fishing trips to Vancouver Island, ski trips to Whistler, and hiking in the Canadian Rockies. Many of these trips cross the Canadian border, where a valid U.S. passport is required for air travel and strongly recommended for any land crossing. These trips are often planned on short notice when conditions align, leaving little time for standard passport processing.",
        tip: "For hunting and fishing trips to British Columbia, a U.S. passport is required for air travel. NEXUS cards work for land crossings but take months to obtain. If you don't have a NEXUS card, a passport is your only option — apply now.",
      },
      {
        icon: "ri-flight-takeoff-line",
        title: "PDX International Departures",
        subtitle: "Portland International Airport — about 28 miles northwest",
        description: "Portland International Airport is the nearest major international departure point for Estacada travelers. PDX offers direct service to Amsterdam, London, Reykjavik, and Vancouver BC, plus connections through major U.S. hubs. Estacada-area families booking summer trips to Europe or winter vacations to Mexico frequently discover that a passport expired — or that a child has never had one — with only weeks before departure.",
        tip: "Book your PDX international flight only after confirming your passport is valid for six months beyond your return date. For Mexico, the six-month rule is often enforced. Don't assume a recently-expired passport is 'close enough.'",
      },
      {
        icon: "ri-ship-line",
        title: "Alaska Cruises from Seattle",
        subtitle: "Summer Alaska cruise departures — a popular Estacada-area family vacation",
        description: "Alaska cruises departing from Seattle are a popular summer vacation for Clackamas County families — relatively close, no flying required, and a spectacular destination. But Alaska cruises require passports for all passengers (or in some cases, enhanced IDs that take months to process). Last-minute cruise deals and group bookings often create passport emergencies for Estacada families who haven't traveled internationally in years.",
        tip: "Alaska cruise lines generally require passports for all passengers, even for closed-loop sailings from Seattle. Enhanced driver licenses (Washington residents can get them; Oregon cannot) are an alternative for land crossings only — not for cruise check-in. Get a passport.",
      },
      {
        icon: "ri-group-line",
        title: "Rural Oregon Family Travel Patterns",
        subtitle: "First-time travelers, extended family reunions, mission trips",
        description: "Rural Clackamas County has a significant population of families who travel internationally infrequently — first-time passport applicants who apply when a once-in-a-decade trip comes up. This includes international family reunions, mission trips, and travel for extended family emergencies. First-time applicants require an in-person acceptance step, which small rural post offices may have limited capacity to accommodate quickly.",
        tip: "First-time passport applicants cannot skip the in-person acceptance clerk step. The Estacada Post Office has limited capacity — plan to use Sandy, Oregon City, or the Clackamas County Clerk for the acceptance step, then rely on Fast Passport Center for expedited processing.",
      },
    ],
    callout: {
      icon: "ri-map-2-line",
      title: "Mt. Hood National Forest & International Recreation Travel",
      description: "Estacada sits at the edge of the Mt. Hood National Forest and is the staging point for some of Oregon's most spectacular outdoor recreation. While domestic recreation doesn't require a passport, many Estacada outdoor enthusiasts extend their adventures into British Columbia — and a valid U.S. passport is required for any Canadian air travel or internationally-connecting outdoor expedition. Fast Passport Center handles urgent passport processing for outdoor travelers on short-notice international schedules.",
    },
  },
  destinations: {
    title: "Where Estacada Travelers Go — And Why Passports Get Urgent",
    intro: "These are the destinations and travel patterns we see most from Estacada and rural Clackamas County clients. Understanding the outdoor recreation and rural travel culture here helps us anticipate and prevent passport emergencies.",
    stats: [
      { stat: "28 mi", label: "to PDX — nearest international airport for Estacada travelers" },
      { stat: "14+ Wks", label: "how long standard processing can take in peak summer season" },
      { stat: "Jun–Sep", label: "peak season for outdoor recreation cross-border travel from Clackamas County" },
      { stat: "24 Hrs", label: "fastest emergency passport processing for eligible Estacada residents" },
    ],
    destinations: [
      { name: "British Columbia, Canada", context: "Hunting in the BC Interior, fishing trips to Vancouver Island, Whistler ski trips, Vancouver sightseeing — all require a valid U.S. passport for air travel", urgency: "Outdoor recreation trips to BC are often booked on short notice when seasons align", icon: "ri-map-pin-2-line" },
      { name: "Alaska — Cruise from Seattle", context: "Summer Alaska cruises from Seattle are popular with Clackamas County families — scenic, accessible, and spectacular", urgency: "Cruise deal bookings happen 2–6 weeks before sailing; passport often overlooked until booking confirmation arrives", icon: "ri-ship-line" },
      { name: "Mexico", context: "Winter beach escapes to Cancún, Cabo, and Riviera Maya — popular for rural Oregon families escaping wet winters", urgency: "Last-minute December–February deals booked with 10–21 days notice", icon: "ri-map-pin-2-line" },
      { name: "Europe", context: "Summer family trips to the UK, France, Germany; mission trips; occasional backpacking adventures for younger Estacada residents", urgency: "Mission trip departures often fall in June–July with passport applications left to the last 4–6 weeks", icon: "ri-map-pin-2-line" },
      { name: "Costa Rica & Central America", context: "Popular eco-adventure destination for Pacific Northwest outdoor enthusiasts — hiking, zip-lining, wildlife tours", urgency: "Adventure travel to Central America tends to be booked 3–6 weeks out by outdoor-focused Oregon travelers", icon: "ri-map-pin-2-line" },
      { name: "Japan & South Korea", context: "Teaching English abroad programs; cultural trips; anime and gaming tourism among younger residents; business travel for tech workers", urgency: "Teaching abroad programs require passports months before departure — many applicants discover expiration at program acceptance", icon: "ri-map-pin-2-line" },
    ],
    crossLinksTitle: "Explore Nearby Oregon Service Areas",
    crossLinksDesc: "We serve travelers throughout Clackamas County and the Portland metro with the same expedited passport processing.",
    crossLinks: [
      { name: "Sandy", route: "/cities/sandy" },
      { name: "Oregon City", route: "/cities/oregon-city" },
      { name: "Gladstone", route: "/cities/gladstone" },
    ],
  },
};